import { Component, type ReactNode } from "react";
import { withTranslation, type WithTranslation } from "react-i18next";

interface Props extends WithTranslation {
  children: ReactNode;
  compact?: boolean;
}

interface State {
  error: Error | null;
}

class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = () => {
    this.setState({ error: null });
    window.location.assign("/");
  };

  reload = () => window.location.reload();

  render() {
    const { error } = this.state;
    const { t, compact } = this.props;
    if (!error) return this.props.children;

    const icon = (
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-rose/10 text-rose">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      </div>
    );

    const devError = import.meta.env.DEV && (
      <pre className="num mt-4 max-h-32 overflow-auto rounded-md bg-canvas p-2 text-start text-[11px] text-rose" dir="ltr">
        {error.message}
      </pre>
    );

    const buttons = (
      <div className="mt-5 flex justify-center gap-2">
        <button
          type="button"
          onClick={this.reload}
          className="rounded-md border-[1.5px] border-line bg-card px-4 py-2 text-[13px] font-semibold text-ink-2 hover:border-primary hover:text-primary"
        >
          {t("error.reload")}
        </button>
        <button
          type="button"
          onClick={this.reset}
          className="rounded-md bg-primary px-4 py-2 text-[13px] font-semibold text-white hover:brightness-110"
        >
          {t("error.goHome")}
        </button>
      </div>
    );

    if (compact) {
      return (
        <div className="grid place-items-center px-6 py-16">
          <div className="max-w-md rounded-lg border border-line bg-card p-8 text-center shadow-card">
            {icon}
            <h1 className="mt-4 text-lg font-extrabold text-ink-1">{t("error.title")}</h1>
            <p className="mt-2 text-[13px] text-ink-2">{t("error.subtitle")}</p>
            {devError}
            {buttons}
          </div>
        </div>
      );
    }

    return (
      <div className="grid min-h-screen place-items-center bg-canvas px-6">
        <div className="max-w-md rounded-lg border border-line bg-card p-8 text-center shadow-card">
          {icon}
          <h1 className="mt-4 text-lg font-extrabold text-ink-1">{t("error.title")}</h1>
          <p className="mt-2 text-[13px] text-ink-2">{t("error.subtitle")}</p>
          {devError}
          {buttons}
        </div>
      </div>
    );
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryInner);
